/**
 * Project-wide content search modal (Ctrl/Cmd+Shift+F).
 *
 * Searches the raw source of every file and component in the current project
 * and lists matching lines grouped by file. Selecting a line hands the match
 * to `onOpen`, which loads the file and jumps to it. Visibility is owned by
 * the ModalManager; this class only drives the modal's contents.
 *
 * Pure matching logic lives in ./project-search.ts.
 */

import { searchProject, snippetAround, findComponentUsages } from './project-search.js';
import type { ProjectSearchInput, ProjectSearchMatch, ProjectSearchResult } from './project-search.js';

export interface ProjectSearchOptions {
  /** Raw content of every project file (unsaved tab content should win) */
  getContents: () => Promise<ProjectSearchInput[]>;
  /** Open `path` and jump to `match` */
  onOpen: (path: string, match: ProjectSearchMatch, query: string, caseSensitive: boolean) => void;
}

const FILE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520Z"/></svg>';
const COMPONENT_ICON = '<svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M320-240 80-480l240-240 57 57-184 184 183 183-56 56Zm320 0-57-57 184-184-183-183 56-56 240 240-240 240Z"/></svg>';

export class ProjectSearch {
  private readonly opts: ProjectSearchOptions;
  private readonly input: HTMLInputElement;
  private readonly caseBtn: HTMLButtonElement;
  private readonly results: HTMLDivElement;
  private readonly empty: HTMLDivElement;
  private readonly summary: HTMLSpanElement;
  private readonly banner: HTMLDivElement;

  private contents: ProjectSearchInput[] = [];
  private caseSensitive = false;
  private debounceTimer: number | null = null;
  /** Selectable match rows, in display order */
  private rows: { el: HTMLElement; path: string; match: ProjectSearchMatch }[] = [];
  private highlighted = -1;
  /** Guards against an older getContents() resolving after a newer open() */
  private loadSeq = 0;
  /** Component path whose usages are listed; null in normal search mode */
  private usagesOf: string | null = null;

  constructor(opts: ProjectSearchOptions) {
    this.opts = opts;
    this.input = document.getElementById('project-search-input') as HTMLInputElement;
    this.caseBtn = document.getElementById('project-search-case') as HTMLButtonElement;
    this.results = document.getElementById('project-search-results') as HTMLDivElement;
    this.empty = document.getElementById('project-search-empty') as HTMLDivElement;
    this.summary = document.getElementById('project-search-summary') as HTMLSpanElement;
    this.banner = document.getElementById('project-search-banner') as HTMLDivElement;

    this.input.addEventListener('input', () => {
      // Typing leaves "usages" mode and goes back to free-text search
      this.setUsagesMode(null);
      this.scheduleSearch();
    });
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.caseBtn.addEventListener('click', () => {
      this.caseSensitive = !this.caseSensitive;
      this.caseBtn.classList.toggle('active', this.caseSensitive);
      this.caseBtn.setAttribute('aria-pressed', String(this.caseSensitive));
      this.runSearch();
      this.input.focus();
    });
  }

  /**
   * Prepare the modal after it is shown: snapshot project contents, prefill
   * the query (keeps the previous query when none is given) and search.
   */
  async open(initialQuery?: string): Promise<void> {
    this.setUsagesMode(null);
    if (initialQuery) this.input.value = initialQuery;
    this.input.focus();
    this.input.select();
    await this.loadContents();
  }

  /**
   * Prepare the modal (after it is shown) to list every file/component that
   * embeds `componentPath` (`_components/...`).
   */
  async showUsages(componentPath: string): Promise<void> {
    this.input.value = '';
    this.setUsagesMode(componentPath);
    this.input.focus();
    await this.loadContents();
  }

  private setUsagesMode(componentPath: string | null): void {
    this.usagesOf = componentPath;
    if (componentPath) {
      const strong = document.createElement('strong');
      strong.textContent = componentPath.replace(/^_components\//, '');
      this.banner.replaceChildren('Usages of component ', strong, ' — type to search all content instead');
      this.banner.classList.remove('hidden');
    } else {
      this.banner.classList.add('hidden');
    }
  }

  private async loadContents(): Promise<void> {
    const seq = ++this.loadSeq;
    this.summary.textContent = 'Loading project…';
    try {
      const contents = await this.opts.getContents();
      if (seq !== this.loadSeq) return;
      this.contents = contents;
    } catch (err) {
      console.error('Project search: failed to load contents', err);
      if (seq !== this.loadSeq) return;
      this.contents = [];
    }
    this.runSearch();
  }

  private scheduleSearch(): void {
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      this.runSearch();
    }, 120);
  }

  private runSearch(): void {
    if (this.usagesOf) {
      const result = findComponentUsages(this.contents, this.usagesOf);
      const name = this.usagesOf.replace(/^_components\//, '');
      this.render(result, `No files embed ${name}`, '');
      if (result.files.length > 0) {
        const n = result.files.length;
        this.summary.textContent = `Embedded ${result.totalMatches}× in ${n} file${n === 1 ? '' : 's'}`;
      }
      return;
    }
    const query = this.input.value;
    const result = searchProject(this.contents, query, { caseSensitive: this.caseSensitive });
    this.render(result, 'No matches for ', query);
  }

  /** `emptyText` + bold `query` is shown when nothing matched; empty query = idle */
  private render(result: ProjectSearchResult, emptyText: string, query: string): void {
    this.results.replaceChildren();
    this.rows = [];
    this.highlighted = -1;

    if (!query && !this.usagesOf) {
      this.empty.classList.add('hidden');
      this.summary.textContent = `${this.contents.length} file${this.contents.length === 1 ? '' : 's'} in project`;
      return;
    }

    if (result.files.length === 0) {
      this.empty.classList.remove('hidden');
      const strong = document.createElement('strong');
      strong.textContent = query;
      const text = this.empty.querySelector('.ps-empty-text')!;
      text.replaceChildren(emptyText, strong);
      this.summary.textContent = 'No results';
      return;
    }
    this.empty.classList.add('hidden');

    const frag = document.createDocumentFragment();
    for (const file of result.files) {
      const group = document.createElement('div');
      group.className = 'project-search-file';

      const header = document.createElement('div');
      header.className = 'project-search-file-header';
      header.title = file.path;
      const isComponent = file.path.startsWith('_components/');
      const displayPath = isComponent ? file.path.slice('_components/'.length) : file.path;
      const slash = displayPath.lastIndexOf('/');
      const name = displayPath.slice(slash + 1);
      const folder = slash === -1 ? '' : displayPath.slice(0, slash).split('/').join(' › ');

      const icon = document.createElement('span');
      icon.className = 'ps-file-icon';
      icon.innerHTML = isComponent ? COMPONENT_ICON : FILE_ICON;
      const nameEl = document.createElement('span');
      nameEl.className = 'project-search-file-name';
      nameEl.textContent = name;
      const folderEl = document.createElement('span');
      folderEl.className = 'project-search-file-folder';
      folderEl.textContent = isComponent ? (folder ? `Components › ${folder}` : 'Components') : folder;
      const count = document.createElement('span');
      count.className = 'project-search-count';
      count.textContent = file.truncated ? `${file.matches.length}+` : String(file.matches.length);
      header.append(icon, nameEl, folderEl, count);
      // Clicking the header opens the first match
      header.addEventListener('click', () => this.openMatch(file.path, file.matches[0]));
      group.appendChild(header);

      for (const match of file.matches) {
        const row = document.createElement('div');
        row.className = 'project-search-match';
        const lineNo = document.createElement('span');
        lineNo.className = 'project-search-line';
        lineNo.textContent = String(match.line);
        const snippet = snippetAround(match);
        const text = document.createElement('span');
        text.className = 'project-search-snippet';
        const mark = document.createElement('mark');
        mark.textContent = snippet.text;
        text.append(snippet.before, mark, snippet.after);
        row.append(lineNo, text);

        const index = this.rows.length;
        row.addEventListener('click', () => this.openMatch(file.path, match));
        row.addEventListener('mousemove', () => {
          if (this.highlighted !== index) this.setHighlight(index, false);
        });
        this.rows.push({ el: row, path: file.path, match });
        group.appendChild(row);
      }
      frag.appendChild(group);
    }
    this.results.appendChild(frag);

    const fileCount = result.files.length;
    this.summary.textContent =
      `${result.totalMatches}${result.capped ? '+' : ''} match${result.totalMatches === 1 ? '' : 'es'} ` +
      `in ${fileCount} file${fileCount === 1 ? '' : 's'}` +
      (result.capped ? ' — refine your search to see all' : '');
    this.setHighlight(0, false);
  }

  private setHighlight(index: number, scroll: boolean): void {
    if (this.rows.length === 0) return;
    const clamped = Math.max(0, Math.min(index, this.rows.length - 1));
    this.rows[this.highlighted]?.el.classList.remove('ps-highlight');
    this.highlighted = clamped;
    const row = this.rows[clamped].el;
    row.classList.add('ps-highlight');
    if (scroll) row.scrollIntoView({ block: 'nearest' });
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setHighlight(this.highlighted + 1, true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setHighlight(this.highlighted - 1, true);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Flush a pending debounce so Enter acts on what's typed
      if (this.debounceTimer !== null) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
        this.runSearch();
      }
      const row = this.rows[this.highlighted];
      if (row) this.openMatch(row.path, row.match);
    }
  }

  private openMatch(path: string, match: ProjectSearchMatch): void {
    // In usages mode, continue the in-file search on the embed reference itself
    const query = this.usagesOf
      ? match.lineText.slice(match.column, match.column + match.length)
      : this.input.value;
    this.opts.onOpen(path, match, query, this.usagesOf ? false : this.caseSensitive);
  }
}
